import { useCallback, useEffect, useState } from 'react'

import { useDispatch } from 'react-redux'

import { getCheckoutComPublicKey } from '@library/environment/isomorphic'

import { actionTypes } from 'actions/actionTypes'
import {
  fireCheckoutError,
  fireCheckoutPendingEvent,
  trackingCardTokenizationSuccessfully,
  trackingCardTokenizationFailed,
} from 'routes/Checkout/checkoutActions'
import { CARD_DETAILS } from 'routes/Checkout/checkoutConfig'
import logger from 'utils/logger'

import { getErrorType, formatAddress } from '../utils/utils'
import { CheckoutStyles } from './CheckoutFrame.styled'
import type {
  FramesAppendedProps,
  FrameCardTokenizedEvent,
  FrameValidationChangedEvent,
  FrameCardTokenizationFailedEvent,
  FrameCardValidationChangedEvent,
} from './types/Frame'

declare global {
  interface Window {
    Frames: FramesAppendedProps
  }
}

const initialState = {
  card_details: {
    showError: false,
    isEmpty: true,
  },
  hasCheckoutFrameStarted: false,
  isCheckoutFormValid: false,
  isCheckoutFormSubmitting: false,
  checkoutScriptFallback: false,
}

type useCheckoutFrameProps = {
  checkoutScriptReady: boolean
  submitOrder: () => void
}

export const useCheckoutFrame = ({
  checkoutScriptReady: hasCheckoutScriptLoaded,
  submitOrder,
}: useCheckoutFrameProps) => {
  const [inputCardNumberError, setInputCardNumberError] = useState(initialState.card_details)
  const [inputExpiryDateError, setInputExpiryDateError] = useState(initialState.card_details)
  const [inputCvvError, setInputCvvError] = useState(initialState.card_details)
  const [isCheckoutFormValid, setIsCheckoutFormValid] = useState<boolean>(
    initialState.isCheckoutFormValid,
  )
  const [isCheckoutFormSubmitting, setIsCheckoutFormSubmitting] = useState<boolean>(
    initialState.isCheckoutFormSubmitting,
  )
  const [hasCheckoutFrameStarted, setHasCheckoutFrameStarted] = useState<boolean>(
    initialState.hasCheckoutFrameStarted,
  )
  const [checkoutScriptFallback, setCheckoutScriptFallback] = useState<boolean>(
    initialState.checkoutScriptFallback,
  )

  const dispatch = useDispatch()

  const handleCardDetailsValidation = () => {
    if (inputCardNumberError.isEmpty) {
      setInputCardNumberError({ ...inputCardNumberError, showError: true })
    }
    if (inputCvvError.isEmpty) {
      setInputCvvError({ ...inputCvvError, showError: true })
    }
    if (inputExpiryDateError.isEmpty) {
      setInputExpiryDateError({
        ...inputExpiryDateError,
        showError: true,
      })
    }
  }

  const handleFrameValidationChanged = useCallback((event: FrameValidationChangedEvent) => {
    const { isEmpty, isValid, element } = event
    switch (element) {
      case 'card-number':
        return setInputCardNumberError({
          showError: !isValid,
          isEmpty,
        })
      case 'expiry-date':
        return setInputExpiryDateError({
          showError: !isValid,
          isEmpty,
        })
      case 'cvv':
        return setInputCvvError({ showError: !isValid, isEmpty })
      default:
        return undefined
    }
  }, [])

  const handleCardValidationChanged = useCallback(
    (event: FrameCardValidationChangedEvent) => {
      setIsCheckoutFormValid(event.isValid)
    },
    [setIsCheckoutFormValid],
  )

  const handleCardTokenized = useCallback(
    (response: FrameCardTokenizedEvent) => {
      dispatch({
        type: actionTypes.PAYMENT_SET_CARD_PAYMENT_TOKEN,
        token: response.token,
      })
      submitOrder()
      window.Frames.enableSubmitForm()
      dispatch(fireCheckoutPendingEvent(actionTypes.CHECKOUT_CARD_SUBMIT, false))
      dispatch(trackingCardTokenizationSuccessfully())
    },
    [dispatch, submitOrder],
  )

  const handleCardTokenizationFailed = (response: FrameCardTokenizationFailedEvent) => {
    const errorMsg = response ? response.message : ''
    const errorType = getErrorType(response.errorCode)

    setIsCheckoutFormSubmitting(false)

    dispatch(fireCheckoutPendingEvent(actionTypes.CHECKOUT_CARD_SUBMIT, false))
    dispatch(fireCheckoutError(errorType))
    dispatch(trackingCardTokenizationFailed(errorMsg))
  }

  const handleSubmitCard = useCallback(
    (options) => {
      setIsCheckoutFormSubmitting(true)
      const { deliveryAddress, values } = options
      const address = formatAddress(deliveryAddress)
      const formValues = values.isBillingAddressDifferent ? values : address

      window.Frames.cardholder = {
        name: values.cardHolderName,
        billingAddress: {
          addressLine1: formValues.houseNo,
          zip: formValues.postcode,
          city: formValues.town,
        },
      }

      dispatch(fireCheckoutPendingEvent(actionTypes.CHECKOUT_CARD_SUBMIT, true))
      window.Frames.submitCard()
        .catch(() => {
          dispatch(fireCheckoutError(actionTypes.VALID_CARD_DETAILS_NOT_PROVIDED))
          dispatch(fireCheckoutPendingEvent(actionTypes.CHECKOUT_CARD_SUBMIT, false))
        })
        .finally(() => setIsCheckoutFormSubmitting(false))
    },
    [dispatch],
  )

  const initializeFrames = useCallback(() => {
    try {
      const config = {
        publicKey: getCheckoutComPublicKey(),
        style: CheckoutStyles,
        localization: {
          cardNumberPlaceholder: 'Card number',
          expiryMonthPlaceholder: 'MM',
          expiryYearPlaceholder: 'YY',
          cvvPlaceholder: 'CVV',
        },
        cardNumber: {
          frameSelector: `[data-frames=${CARD_DETAILS.CARD_NUMBER}]`,
        },
        expiryDate: {
          frameSelector: `[data-frames=${CARD_DETAILS.EXPIRY_DATE}]`,
        },
        cvv: {
          frameSelector: `[data-frames=${CARD_DETAILS.CVV}]`,
        },
        frameValidationChanged: handleFrameValidationChanged,
        cardValidationChanged: handleCardValidationChanged,
        cardTokenized: handleCardTokenized,
        cardTokenizationFailed: handleCardTokenizationFailed,
        ready: () => {
          setHasCheckoutFrameStarted(true)
        },
      }

      if (window.Frames) {
        window.Frames.init(config)
      }
    } catch (error: Error | unknown) {
      ;(logger as Record<string, any>).error({ message: (error as Error).message, errors: error })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    handleCardTokenized,
    handleCardValidationChanged,
    handleFrameValidationChanged,
    setHasCheckoutFrameStarted,
  ])

  useEffect(() => {
    if (!hasCheckoutFrameStarted && hasCheckoutScriptLoaded && !checkoutScriptFallback) {
      setCheckoutScriptFallback(true)
      initializeFrames()
    }
  }, [initializeFrames, hasCheckoutScriptLoaded, hasCheckoutFrameStarted, checkoutScriptFallback])

  return {
    formValidationError: {
      showCardNumberError: inputCardNumberError.showError,
      showCVVError: inputCvvError.showError,
      showExpiryDateError: inputExpiryDateError.showError,
    },
    isCheckoutFormValid,
    isCheckoutFormSubmitting,
    handleSubmitCard,
    handleCardDetailsValidation,
    hasCheckoutFrameStarted,
    hasCheckoutScriptLoaded,
    handleCardTokenizationFailed,
    handleCardTokenized,
    handleFrameValidationChanged,
  }
}
